import { useState, useCallback } from 'react';

type UseSelectableTable<T> = {
  selectedItems: Set<string | number>;
  handleSelectAll: (checked: boolean) => void;
  handleSelectItem: (item: T, checked: boolean) => void;
  bulkSelectItems: (items: T[]) => void;
  isAllSelected: boolean;
  clearSelection: () => void;
};

export function useSelectableTable<T extends Record<string, any>>(
  data: T[],
  getItemId: (item: T) => string | number = (item) => item.id,
): UseSelectableTable<T> {
  // Persist selection across pages
  const [selectedItems, setSelectedItems] = useState<Set<string | number>>(
    new Set(),
  );

  // Select or deselect all items on the current page
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);
        if (checked) {
          data.forEach((item) => newSelected.add(getItemId(item)));
        } else {
          data.forEach((item) => newSelected.delete(getItemId(item)));
        }
        return newSelected;
      });
    },
    [data, getItemId],
  );

  // Select or deselect a single item
  const handleSelectItem = useCallback(
    (item: T, checked: boolean) => {
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);
        const id = getItemId(item);
        if (checked) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
        return newSelected;
      });
    },
    [getItemId],
  );

  // Bulk select a list of items (e.g., when pre-selecting)
  const bulkSelectItems = useCallback(
    (items: T[]) => {
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);
        items.forEach((item) => newSelected.add(getItemId(item)));
        return newSelected;
      });
    },
    [getItemId],
  );

  // Check if all items on the current page are selected
  const isAllSelected =
    data.length > 0 && data.every((item) => selectedItems.has(getItemId(item)));

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);
  return {
    selectedItems,
    handleSelectAll,
    handleSelectItem,
    isAllSelected,
    clearSelection,
    bulkSelectItems,
  };
}
